// CRM-owned projects. Backed by /api/v1/crm/crm-projects — fully decoupled
// from TG's host Project model. Shape kept compatible with the prior TG-
// projects hook ({ _id, name, status, description }) so consumers (Leads,
// Pipeline, Settings, etc.) don't have to change.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "../api/client";
import { useSelectedCompany } from "../contexts/SelectedCompanyProvider";

const LIST_KEY = ["crm", "projects"];

// Reactive read of the auth token from localStorage. The CRM provider stack
// is mounted at App.js level (above the router) so an in-progress browser
// call survives navigation — which means useProjects() exists on the login
// screen too. Without this gate it would fire a token-less request, error
// out, and leave isFetched=true + projects=[] in the cache; when the user
// then logged in and opened CRM, the welcome empty-state would flash before
// a refetch could replace it. Listening for the 'storage' event (fires on
// other tabs) and a custom 'auth-token-changed' event (fires on this tab —
// AuthContext.login dispatches it) keeps this in sync with login/logout.
function useAuthToken() {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem("token") || null; } catch { return null; }
  });
  useEffect(() => {
    const sync = () => {
      try { setToken(localStorage.getItem("token") || null); } catch { setToken(null); }
    };
    window.addEventListener("storage", sync);
    window.addEventListener("auth-token-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("auth-token-changed", sync);
    };
  }, []);
  return token;
}

export function useProjects({ status } = {}) {
  const token = useAuthToken();
  const { companyQueryParams, isLoading: companyLoading } = useSelectedCompany();
  const scopeKey = companyQueryParams || {};
  const q = useQuery({
    queryKey: status ? [...LIST_KEY, { status, ...scopeKey }] : [...LIST_KEY, scopeKey],
    enabled: !!token && !companyLoading,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (scopeKey.unassigned) params.set("unassigned", "true");
      else if (scopeKey.companyId) params.set("companyId", scopeKey.companyId);
      const qs = params.toString();
      const { data } = await crmApi.get(`/crm-projects${qs ? `?${qs}` : ""}`);
      return Array.isArray(data?.items) ? data.items : [];
    },
    staleTime: 5 * 60_000,
    // Fresh-login race window: a brand-new login can hit CRM before the
    // backend bootstraps the user's CrmMembership row (the gate middleware
    // creates it on the first authenticated CRM request, but other requests
    // racing in get 403 until that finishes). We retry aggressively so the
    // user never has to manually refresh — a stale-data flash is fine, an
    // incorrect "Create your first project" empty state is not.
    retry: (failureCount, err) => {
      if (failureCount >= 3) return false;
      const status = err?.response?.status;
      // Retry transient errors: 401 (token not propagated yet), 403
      // (membership not bootstrapped yet), 5xx, network. Skip 404/400.
      // crm_not_enabled is a definitive answer, not a bootstrap race.
      if (err?.response?.data?.error === "crm_not_enabled") return false;
      if (status === 401 || status === 403) return true;
      if (!status) return true; // network error
      if (status >= 500) return true;
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(400 * 2 ** attemptIndex, 2000),
    refetchOnMount: true,
  });

  const byId = useMemo(() => {
    const m = new Map();
    (q.data || []).forEach((p) => m.set(String(p._id), p));
    return m;
  }, [q.data]);

  return { ...q, projects: q.data || [], byId };
}

export function formatProject(p) {
  if (!p) return "—";
  return p.name || p.title || "Untitled project";
}

// Project CUD affects a lot more than the project list itself: pipelines are
// project-scoped, leads are project-scoped, the dashboard rolls up per-project
// counts, members may be project-scoped, and filter-options change per
// project. Fan out so consumers of any of those queries re-fetch.
function invalidateProjects(qc) {
  qc.invalidateQueries({ queryKey: LIST_KEY });
  qc.invalidateQueries({ queryKey: ["crm", "pipelines"] });
  qc.invalidateQueries({ queryKey: ["crm", "leads"] });
  qc.invalidateQueries({ queryKey: ["crm", "dashboard", "overview"] });
  qc.invalidateQueries({ queryKey: ["crm", "members"] });
  qc.invalidateQueries({ queryKey: ["crm", "project-members"] });
  qc.invalidateQueries({ queryKey: ["crm", "leads", "filter-options"] });
}

export function useCreateProject() {
  const qc = useQueryClient();
  const { companyQueryParams } = useSelectedCompany();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await crmApi.post("/crm-projects", payload, { params: companyQueryParams });
      return data?.project;
    },
    onSuccess: () => invalidateProjects(qc),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, patch }) => {
      const { data } = await crmApi.patch(`/crm-projects/${projectId}`, patch);
      return data?.project;
    },
    onSuccess: () => invalidateProjects(qc),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (projectId) => {
      const { data } = await crmApi.delete(`/crm-projects/${projectId}`);
      return data;
    },
    onSuccess: () => invalidateProjects(qc),
  });
}
